import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { browseCatalogue, addToList, getList } from "../../services/portalService";
import { errorToast, successToast } from "../Toasts/Toasts";

// What a society can order, with a picture and their price.
//
// Only plants ManBhawan has approved appear here, and only their name, size
// and price - never what we paid, who we buy from, or anything else from the
// vendor side. Where a society has a negotiated rate, that is the price shown
// and it says so.
//
// The price is indicative, and the page says so plainly (MG, 23 Sep 2026).
// It is landed - what the plant costs us delivered to the nursery, vendor
// transport included - plus margin. What it does not carry is GST, and the
// delivery that matters to the customer: nursery to their site, which
// depends on where they are and how much they take.
//
// It also promises nothing about stock. A nursery is not a warehouse, and
// what is available this week at this size is a real question, so picking
// from here starts a request; the quotation is where availability, timeline
// and the formal figure are settled.

const inr = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// How many? The question a manager without a gardener cannot answer, asked
// the other way round: they can measure a lawn or pace a boundary, so ask for
// that and work the quantity out. Grass covers a square foot per square foot;
// a hedge takes so many plants per running foot. An item with no conversion
// set just takes a number, as before.
const Measure = ({ row, onQuantity }) => {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  if (!row.coverage || !row.coverageBasis) return null;

  const area = row.coverageBasis === "sq ft";
  const measured = area ? Number(a || 0) * Number(b || 0) : Number(a || 0);
  const needed = measured > 0 ? Math.ceil(measured * Number(row.coverage)) : 0;
  const short = needed > 0 && row.minOrder && needed < row.minOrder;

  return (
    <div className="mt-2 p-2 rounded" style={{ background: "#f4f8f5" }}>
      <div className="small fw-semibold mb-1">{area ? "Measure the area" : "Measure the length"}</div>
      <div className="d-flex align-items-center gap-1">
        <Form.Control size="sm" type="number" min="0" inputMode="numeric" placeholder={area ? "length ft" : "feet"} value={a} onChange={(e) => setA(e.target.value)} />
        {area && <><span className="small text-muted">×</span>
          <Form.Control size="sm" type="number" min="0" inputMode="numeric" placeholder="width ft" value={b} onChange={(e) => setB(e.target.value)} /></>}
      </div>
      {needed > 0 && (
        <div className="small mt-2">
          {area ? `${measured.toLocaleString("en-IN")} sq ft` : `${measured} running ft`} →{" "}
          <b>{needed.toLocaleString("en-IN")} {row.unit === "each" ? (needed === 1 ? "plant" : "plants") : row.unit}</b>
          {short && <div className="text-warning">Below our {row.minOrder} {row.unit} minimum — we will quote it, but a smaller job may not be worth the trip.</div>}
          <Button size="sm" variant="outline-success" className="mt-1 w-100" onClick={() => onQuantity(needed)}>Use this</Button>
        </div>
      )}
    </div>
  );
};

const Plants = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [qty, setQty] = useState({}); // variantId -> what is typed, before it is added
  const [onList, setOnList] = useState({}); // variantId -> quantity already on the list
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await browseCatalogue()); } catch (e) { errorToast("Could not load the plant list"); setRows([]); }
    try {
      const list = await getList();
      setOnList(Object.fromEntries((list.items || []).map((i) => [i.variantId, i.quantity])));
    } catch (e) { /* an empty list is not an error worth shouting about */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => [...new Set((rows || []).map((r) => r.category).filter(Boolean))].sort(), [rows]);
  const shown = (rows || []).filter(
    (r) =>
      (!category || r.category === category) &&
      (!q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.description || "").toLowerCase().includes(q.toLowerCase()))
  );

  // Grouped by the job rather than the botany (MG, 23 Sep 2026): a society
  // manager may have no gardener to ask, so the heading has to say what the
  // thing is for - "Screening & hedging", "Lawn & turf" - not what family it
  // belongs to. Category first, then the shelf inside it.
  const groups = useMemo(() => {
    const by = new Map();
    for (const row of shown) {
      const key = row.category || "Everything else";
      if (!by.has(key)) by.set(key, new Map());
      const shelf = row.subCategory || "";
      const inner = by.get(key);
      if (!inner.has(shelf)) inner.set(shelf, []);
      inner.get(shelf).push(row);
    }
    return [...by.entries()]
      .sort((a, b) => (a[0] === "Everything else" ? 1 : b[0] === "Everything else" ? -1 : a[0].localeCompare(b[0])))
      .map(([name, inner]) => [name, [...inner.entries()].sort((a, b) => a[0].localeCompare(b[0]))]);
  }, [shown]);

  // Adding writes to the list on the server, so it is still there tomorrow,
  // on another phone, or after the browser is closed.
  const add = async (product, size) => {
    const wanted = Math.max(1, Math.round(Number(qty[size.variantId]) || 1));
    setBusy(true);
    try {
      const list = await addToList({ productId: product.productId, variantId: size.variantId, quantity: wanted });
      setOnList(Object.fromEntries((list.items || []).map((i) => [i.variantId, i.quantity])));
      setQty((p) => ({ ...p, [size.variantId]: "" }));
      successToast(`${product.name} added to your list`);
    } catch (e) { errorToast(e?.message || "Could not add that"); } finally { setBusy(false); }
  };

  const listCount = Object.keys(onList).length;

  if (rows === null) return null;

  return (
    <div className="portal-page">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="fw-bold mb-0">Plants</h5>
          <div className="small text-muted">
            Indicative prices, before GST and delivery to your site. Not sure how many? Measure the area or the boundary and we will work it out. Tell us what you need and when, and we will confirm what is available and send a formal quotation.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Form.Control size="sm" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 200 }} />
          {categories.length > 1 && (
            <Form.Select size="sm" value={category} onChange={(e) => setCategory(e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">All</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          )}
        </div>
      </div>

      {!shown.length && <div className="text-muted">Nothing to show yet. Send us what you need and we will quote it.</div>}

      {groups.map(([groupName, shelves]) => (
        <section key={groupName} className="mb-4">
          <div className="d-flex align-items-baseline gap-2 mb-2">
            <h6 className="fw-bold mb-0">{groupName}</h6>
            <span className="small text-muted">{shelves.reduce((a, [, list]) => a + list.length, 0)}</span>
          </div>
          {shelves.map(([shelfName, items]) => (
          <div key={shelfName || "_"} className="mb-3">
          {shelfName && <div className="small fw-semibold text-muted mb-2">{shelfName}</div>}
          <div className="row g-3">
            {items.map((row) => (
              <div className="col-6 col-md-4 col-lg-3" key={row.productId}>
                <div className="card h-100">
                  {row.image
                    ? <img src={row.image} alt={row.name} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", background: "#f3f6f4" }} />
                    : <div style={{ width: "100%", aspectRatio: "1", background: "#eef3ef" }} />}
                  <div className="card-body p-2">
                    <div className="fw-semibold" style={{ fontSize: 15 }}>{row.name}</div>
                    {row.description && <div className="small text-muted">{row.description}</div>}
                    {row.sizes.map((size) => (
                      <div key={size.variantId} className="d-flex justify-content-between align-items-center mt-2 gap-2">
                        <div className="small">
                          {size.size}
                          <div className="fw-semibold">
                            {inr(size.price)}<span className="text-muted fw-normal">{row.unit && row.unit !== "each" ? ` / ${row.unit}` : ""}*</span>
                            {size.yourPrice && <span className="badge bg-success ms-1">your rate</span>}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <Form.Control
                            size="sm"
                            type="number"
                            min="1"
                            inputMode="numeric"
                            style={{ width: 62 }}
                            value={qty[size.variantId] || ""}
                            onChange={(e) => setQty((p) => ({ ...p, [size.variantId]: e.target.value }))}
                            placeholder="qty"
                          />
                          <Button size="sm" variant="outline-success" disabled={busy} onClick={() => add(row, size)}>Add</Button>
                        </div>
                      </div>
                    ))}
                    <Measure row={row} onQuantity={(n) => setQty((p) => ({ ...p, [row.sizes[0].variantId]: String(n) }))} />
                    {row.sizes.some((s) => onList[s.variantId]) && (
                      <div className="small text-success mt-1">
                        On your list: {row.sizes.filter((s) => onList[s.variantId]).map((s) => `${onList[s.variantId]} × ${s.size}`).join(", ")}
                      </div>
                    )}
                    {row.careNotes && <div className="small text-muted mt-2">{row.careNotes}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
          ))}
        </section>
      ))}

      {shown.length > 0 && (
        <div className="small text-muted mt-3">* Indicative, before GST and delivery to your site. Availability and delivery time are confirmed on the quotation.</div>
      )}

      {listCount > 0 && (
        <div className="position-sticky bottom-0 py-3 mt-3 d-flex justify-content-between align-items-center" style={{ background: "linear-gradient(transparent, #f6f8f6 40%)" }}>
          <div className="small">{listCount} item{listCount > 1 ? "s" : ""} on your list</div>
          <Link to="/portal/list" className="btn btn-success">Review your list</Link>
        </div>
      )}
    </div>
  );
};

export default Plants;
